<template>
  <div
    ref="dataTable"
    :style="'--nbColumns:' + (columns && Object.values(columns).length ? Object.values(columns).length : 0)"
  >
    <FiltersDataTableComponent
      v-if="showFilters"
      :showFilterInput="showFilterInput && rows && rows.length"
      :showLimitSelect="rows && Object.keys(rows).length"
      :count="count"
      :defaultLimit="limit"
      @limitList="limitList"
      @search="search"
    />
    <HeadingDataTableComponent v-if="columns && Object.keys(columns).length" :columns="columns" @sort="sort" />
    <template v-if="rows && Object.keys(rows).length">
      <BodyDataTableComponent
        :rowList="rows"
        :actions="actions"
        :columns="columns"
        @emitRowAction="emitRowAction"
        @clickAction="clickAction"
      />
      <PaginationDataTableComponent
        v-if="count"
        :totalNumberOfPage="totalNumberOfPage"
        :activePage="activePage"
        :count="count"
        :limit="limit"
        @changePage="changePage"
      />
    </template>
    <EmptyDataTableComponent v-else :emptyLabel="emptyLabel" />
  </div>
  <BlockLoader :loading="loading" />
</template>

<script>
import HeadingDataTableComponent from "@/components/data-table/HeadingDataTableComponent.vue";
import BodyDataTableComponent from "@/components/data-table/BodyDataTableComponent.vue";
import PaginationDataTableComponent from "@/components/data-table/PaginationDataTableComponent.vue";
import { dataTableManager } from "@/services/data-table/data-table-manager";
import FiltersDataTableComponent from "@/components/data-table/FiltersDataTableComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import EmptyDataTableComponent from "@/components/data-table/EmptyDataTableComponent.vue";

export default {
  name: "DataTableComponent",
  components: {
    EmptyDataTableComponent,
    BlockLoader,
    FiltersDataTableComponent,
    PaginationDataTableComponent,
    BodyDataTableComponent,
    HeadingDataTableComponent,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rowList: {
      type: Array,
      required: true,
    },
    actions: {
      type: Array,
      required: false,
      default: () => [],
    },
    limit: {
      type: Number,
      required: false,
      default: 10,
    },
    count: {
      type: Number,
      required: false,
      default: null,
    },
    apiFilters: {
      type: Boolean,
      required: false,
      default: true,
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    showFilterInput: {
      type: Boolean,
      required: false,
      default: true,
    },
    dataTableCode: {
      type: String,
      required: false,
      default: "",
    },
    emptyLabel: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["refresh"],
  data() {
    return {
      sliceLimit: this.limit,
      rows: [],
      sortType: "ASC",
      sortColumn: null,
      activePage: 1,
      offset: 0,
      keywordSearch: "",
    };
  },
  computed: {
    totalNumberOfPage() {
      return Math.ceil(this.count / this.limit);
    },
  },
  watch: {
    rowList() {
      this.initRows();
    },
  },
  mounted() {
    this.initRows();
  },
  methods: {
    scrollToTableTop(scroll = true) {
      if (scroll) {
        const $this = this;
        setTimeout(function () {
          if ($this.$refs.dataTable) {
            window.scrollTo({
              behavior: "smooth",
              top: $this.$refs.dataTable.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 130,
            });
          }
        }, 50);
      }
    },

    initRows() {
      if (!this.apiFilters) {
        this.rows = dataTableManager.processRows(this.sliceLimit, this.rowList, this.limit, this.offset);
      } else {
        this.rows = this.rowList;
      }
    },

    emitRowAction(payload) {
      this.$emit(payload.action, { item: payload.item, id: payload.id });
    },

    clickAction(payload) {
      this.$emit(payload.action, payload.params);
    },

    limitList(limit) {
      this.sliceLimit = limit;
      this.offset = 0;
      this.activePage = 1;
      this.refreshList();
    },

    search(payload) {
      this.keywordSearch = payload;
      this.activePage = 1;
      this.refreshList();
    },

    refreshList() {
      const search = this.keywordSearch;
      const params = dataTableManager.getListParams(
        null,
        this.sliceLimit,
        this.sortType,
        this.sortColumn,
        search,
        this.offset,
      );

      this.$emit("refresh", { params, code: this.dataTableCode });
    },

    changePage(payload) {
      this.activePage = payload;
      this.offset = (payload - 1) * this.limit;
      this.scrollToTableTop();
      if (!this.apiFilters) {
        this.rows = dataTableManager.processRows(this.sliceLimit, this.rowList, this.limit, this.offset);
      } else {
        this.refreshList();
      }
    },

    initSort() {
      Object.values(this.columns).forEach(function (column) {
        column.sortWay = "";
      });
    },

    sort(column, columnName) {
      dataTableManager.resetSort(this.columns);

      if (column.sortable) {
        const sortResult = dataTableManager.sort(column, columnName, this.sortType, this.apiFilters);

        if (sortResult.initSort) {
          this.sortType = sortResult.sortType;
          this.sortColumn = sortResult.sortColumn;
          column.sortWay = sortResult.sortWay;
        }

        if (sortResult.shouldRefreshList) {
          this.refreshList();
        }
      }
      this.activePage = 1;
    },
  },
};
</script>

<style lang="scss" scoped></style>
